/**
 * Parse user-provided JSON into a format suitable for txViz.
 *
 * Handles BigInt conversion since JSON.parse() cannot handle BigInt natively.
 * Supports two formats:
 *   1. Strings ending with "n": "1000000000000000000n" → 1000000000000000000n
 *   2. Known BigInt fields are auto-converted from numbers
 */

/** Fields that should always be BigInt for Ethereum */
const ETHEREUM_BIGINT_FIELDS = new Set([
  'chainId', 'nonce', 'value', 'gasLimit', 'gasPrice',
  'maxFeePerGas', 'maxPriorityFeePerGas', 'maxFeePerBlobGas',
]);

/** Fields that should always be BigInt for Bitcoin */
const BITCOIN_BIGINT_FIELDS = new Set(['value']);

/**
 * Parse JSON with BigInt support.
 * Strings ending with 'n' are converted to BigInt.
 * Known numeric fields for the selected chain are also converted.
 */
export function parseJsonWithBigInt(jsonStr: string, chain: string): unknown {
  // Pre-process: convert BigInt literal notation (123n) in JSON
  // This handles cases where users write values like: "value": 1000000000000000000n
  // which is not valid JSON, by wrapping them in strings first
  const preprocessed = jsonStr.replace(
    /:\s*(\d+)n\b/g,
    ': "$1n"'
  );

  const bigintFields = chain === 'ethereum'
    ? ETHEREUM_BIGINT_FIELDS
    : chain === 'bitcoin'
      ? BITCOIN_BIGINT_FIELDS
      : new Set<string>();

  return JSON.parse(preprocessed, (key, value) => {
    // String ending with 'n' → BigInt
    if (typeof value === 'string' && /^\d+n$/.test(value)) {
      return BigInt(value.slice(0, -1));
    }

    // Known BigInt fields with numeric values
    if (typeof value === 'number' && bigintFields.has(key)) {
      return BigInt(value);
    }

    return value;
  });
}

/**
 * Stringify an object with BigInt support for display.
 */
export function stringifyWithBigInt(obj: unknown): string {
  return JSON.stringify(obj, (_key, value) => {
    if (typeof value === 'bigint') {
      return `${value.toString()}n`;
    }
    return value as unknown;
  }, 2);
}
